import {
  ApiErrorText,
  Button,
  CopyField,
  DeltaLabel,
  IconTextItem,
  Loader,
  ModalComponentWrapper,
  Skeleton,
  SearchableSelect,
} from '@/shared/components';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/shared/components/ui/tooltip';
import {
  useAssetPrices,
  useAssets,
  useAssetsIcons,
  useCryptoDepositAddress,
  useGenerateCryptoDepositAddress,
} from '@/shared/api';
import { useQueryClient } from '@tanstack/react-query';
import type { AssetsAsset, CryptoDepositAddressResponse } from '@/shared/types';
import { useSystemStatusStore } from '@/entities/system';
import RefreshIcon from '@/shared/assets/exchange-process-icon.svg?react';
import {
  GetNewAddressDialog,
  type GetNewAddressStep,
} from './GetNewAddressDialog';
import { cn } from '@/shared/utils';
import { useDepositWithdrawAssetsDictionary } from '@/shared/api/queries/useDepositWithdrawAssetsDictionary.ts';
import type {
  AssetOption,
  DepositCryptoModalProps,
  NetworkOption,
} from '@/features/deposit-crypto/model';
import {
  formatReceivedPrice,
  useAppSettingsStore,
  useOperationsPreSelectAsset,
} from '@/shared/lib';

export function DepositCryptoModal({
  onBack,
  title,
  onClose,
  assetCode,
}: DepositCryptoModalProps) {
  const { t } = useTranslation();
  const { baseAsset } = useAppSettingsStore();

  const { assetsIconsData } = useAssetsIcons();
  const { data: usdtPriceData, isLoading: isUsdtLoading } = useAssetPrices({
    asset: baseAsset,
  });
  const {
    depositWithdrawAssetsData: data,
    depositWithdrawAssetsLoading: isLoading,
    depositWithdrawAssetsIsError: isError,
    depositWithdrawAssetsError: error,
  } = useDepositWithdrawAssetsDictionary();

  const { data: assetsData } = useAssets({
    enabled: !!data,
  });

  const assetNetworksMap = useMemo(() => {
    const map: Record<string, NetworkOption[]> = {};
    const networks = data?.value?.deposit?.crypto || [];

    networks.forEach(network => {
      network.assets?.forEach(asset => {
        if (!map[asset.code]) {
          map[asset.code] = [];
        }

        const existingNetwork = map[asset.code].find(
          n => n.code === network.networkCode
        );
        if (!existingNetwork) {
          map[asset.code].push({
            code: network.networkCode,
            name: network.networkName,
            icon: network.networkIcon,
            assetCode: asset.code,
            minConfirm: network.minConfirm,
          });
        }
      });
    });

    return map;
  }, [data]);

  const uniqueAssetCodes = useMemo(() => {
    return Object.keys(assetNetworksMap);
  }, [assetNetworksMap]);

  const assetOptions = useMemo<AssetOption[]>(() => {
    if (!usdtPriceData || !assetsData || !assetsIconsData) {
      return [];
    }

    return uniqueAssetCodes.map((assetCode: string) => {
      const assetInfo = assetsData.value?.find(
        (asset: AssetsAsset) => asset.code === assetCode
      );

      const priceAsset = usdtPriceData.assets?.find(
        asset => asset.code === assetCode
      );

      return {
        code: assetCode,
        value: assetCode,
        name: assetInfo?.name || assetCode,
        basePrecission:
          priceAsset?.precision || usdtPriceData.basePrecission || 0,
        percentChange24h: priceAsset?.percentChange24h || 0,
        icon: assetsIconsData.value?.[assetCode] || '',
        baseRate: priceAsset?.baseRate || 0,
      };
    });
  }, [uniqueAssetCodes, assetsData, assetsIconsData, usdtPriceData]);

  const [selectedAsset, setSelectedAsset] = useState<AssetOption | undefined>(
    undefined
  );
  const [selectedNetwork, setSelectedNetwork] = useState<
    NetworkOption | undefined
  >(() => undefined);

  const availableNetworks = useMemo(() => {
    return selectedAsset ? assetNetworksMap[selectedAsset.code] || [] : [];
  }, [selectedAsset, assetNetworksMap]);

  const {
    data: depositAddressData,
    isLoading: isAddressLoading,
    error: addressError,
  } = useCryptoDepositAddress({
    networkName: selectedNetwork?.name || '',
    assetCode: selectedAsset?.code || '',
    enabled: !!selectedNetwork?.code && !!selectedAsset?.code,
  });

  const queryClient = useQueryClient();
  const [newAddressStep, setNewAddressStep] =
    useState<GetNewAddressStep | null>(null);
  const [isNewAddressGenerated, setIsNewAddressGenerated] = useState(false);
  const [newAddressError, setNewAddressError] = useState('');
  const [generatedAddress, setGeneratedAddress] =
    useState<CryptoDepositAddressResponse | null>(null);

  const { mutateAsync: generateNewAddress, isPending: isGeneratingAddress } =
    useGenerateCryptoDepositAddress();

  useEffect(() => {
    setIsNewAddressGenerated(false);
  }, [selectedAsset?.code, selectedNetwork?.code]);

  const handleGenerateNewAddress = async () => {
    if (!selectedNetwork || !selectedAsset) return;

    try {
      const response = await generateNewAddress({
        networkName: selectedNetwork.name,
        assetCode: selectedAsset.code,
      });

      if (response.cryptoAddress === depositAddressData?.cryptoAddress) {
        setNewAddressStep('same');
      } else {
        setGeneratedAddress(response);
        setNewAddressStep('success');
      }
    } catch (error) {
      if (useSystemStatusStore.getState().status !== 'ok') {
        setNewAddressStep(null);
        return;
      }

      setNewAddressError(error instanceof Error ? error.message : '');
      setNewAddressStep('error');
    }
  };

  const handleNewAddressDialogClose = () => {
    if (
      newAddressStep === 'success' &&
      generatedAddress &&
      selectedNetwork &&
      selectedAsset
    ) {
      queryClient.setQueryData(
        ['cryptoDepositAddress', selectedNetwork.name, selectedAsset.code],
        generatedAddress
      );
      setIsNewAddressGenerated(true);
      setGeneratedAddress(null);
    }

    setNewAddressStep(null);
  };

  const isGetNewAddressDisabled = isGeneratingAddress || isNewAddressGenerated;

  useOperationsPreSelectAsset({
    assetCode,
    options: assetOptions,
    setValue: code =>
      setSelectedAsset(assetOptions.find(asset => asset.code === code)),
  });

  useEffect(() => {
    if (!selectedAsset) {
      setSelectedNetwork(undefined);
      return;
    }

    const preselectedNetwork =
      availableNetworks.length === 1 ? availableNetworks[0] : undefined;

    if (
      preselectedNetwork &&
      preselectedNetwork.assetCode === selectedAsset.code
    ) {
      setSelectedNetwork(preselectedNetwork);
    } else {
      setSelectedNetwork(undefined);
    }
  }, [availableNetworks, selectedAsset?.code]);

  const networksDisabled = availableNetworks.length <= 1;

  return (
    <>
      <ModalComponentWrapper
        title={title}
        onBack={onBack}
        footer={
          <Button
            variant={'secondary'}
            className="headline4 px-[3.2rem] py-[1.6rem] rounded-[1.2rem] w-full"
            onClick={() => onClose && onClose()}>
            {t('depositMethods.crypto.submitButton')}
          </Button>
        }>
        <>
          {isUsdtLoading || isLoading ? (
            <Loader />
          ) : isError ? (
            <ApiErrorText error={error} size="small" />
          ) : (
            <>
              <div className="flex flex-col gap-[1.6rem]">
                <SearchableSelect<AssetOption>
                  floatingLabel={t('depositMethods.crypto.assetLabel')}
                  contentViewportClassName="max-h-[calc(var(--radix-dropdown-menu-content-available-height)-1rem)]"
                  value={selectedAsset?.value}
                  onValueChange={val => {
                    const newAsset = assetOptions.find(
                      asset => asset.value === val
                    );
                    setSelectedNetwork(undefined);
                    setSelectedAsset(newAsset);
                  }}
                  searchFilter={(option, search) =>
                    option.code.toLowerCase().indexOf(search.toLowerCase()) !==
                    -1
                  }
                  items={assetOptions}
                  renderItem={asset => (
                    <div className="flex items-center justify-between w-full">
                      <IconTextItem {...asset} title={asset.code} />
                      <div className="flex flex-col items-end">
                        <div className="flex items-center gap-1">
                          <div className="headline4">
                            {formatReceivedPrice(asset.baseRate)}
                            <span> {baseAsset}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <DeltaLabel
                            deltaPercent={asset.percentChange24h}
                            size="small"
                            label={`(24${t('common.hoursShort')})`}
                            className="bg-transparent p-0 p4"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                  renderSelectedValue={value => {
                    const asset = assetOptions.find(
                      asset => asset.value === value
                    );

                    if (!asset) return <></>;

                    return (
                      <div className="flex items-center justify-between w-full">
                        <IconTextItem
                          icon={asset.icon}
                          title={asset.code}
                          variant="compact"
                        />
                      </div>
                    );
                  }}
                />

                {selectedAsset && availableNetworks.length > 0 && (
                  <div className="flex flex-col gap-[0.4rem]">
                    <Select
                      value={selectedNetwork?.code || ''}
                      onValueChange={val => {
                        setSelectedNetwork(
                          availableNetworks.find(
                            network => network.code === val
                          )
                        );
                      }}>
                      <SelectTrigger
                        disabled={networksDisabled}
                        size="md"
                        className="headline4 w-full h-[6rem] rounded-[1.6rem] bg-muted px-6"
                        floatingLabel={
                          t('depositMethods.crypto.networkLabel') as string
                        }
                        value={selectedNetwork?.code}>
                        {selectedNetwork ? (
                          <IconTextItem
                            title={selectedNetwork.code}
                            variant="compact"
                            className={cn(
                              networksDisabled ? 'text-muted-foreground' : ''
                            )}
                          />
                        ) : (
                          <SelectValue />
                        )}
                      </SelectTrigger>

                      <SelectContent className="rounded-[1.6rem] border border-border">
                        {availableNetworks.map(network => (
                          <SelectItem
                            key={network.code}
                            value={network.code}
                            className="px-6 py-3 h-auto rounded-lg">
                            <div className="flex items-center justify-between w-full">
                              <IconTextItem
                                title={network.code}
                                name={network.name}
                                variant="default"
                              />
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {selectedNetwork?.minConfirm !== undefined && (
                      <div className="p5 px-[1.6rem] text-secondary">
                        <span>
                          {t('depositMethods.crypto.networkConfirmations')}:
                        </span>
                        <span> {selectedNetwork.minConfirm}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {isAddressLoading && selectedAsset && selectedNetwork && (
                <div className="gap-[1.6rem] flex flex-col">
                  <Skeleton
                    variant="medium"
                    className="w-[70%] h-[2.4rem] rounded-[1.2rem]"
                  />
                  <Skeleton
                    variant="medium"
                    className="w-full h-[14.4rem] rounded-[1.2rem]"
                  />
                </div>
              )}

              {addressError && (
                <ApiErrorText error={addressError} size="small" />
              )}

              {!isAddressLoading && depositAddressData?.cryptoAddress && (
                <div className="flex flex-col gap-[1.6rem]">
                  <div className="p5 text-muted-foreground gap-1">
                    {t('depositMethods.crypto.note') as string}
                    <span className="headline6 text-foreground ml-1">
                      ({selectedNetwork?.name} {selectedNetwork?.code})
                    </span>
                  </div>
                  <div className="flex flex-col gap-[1.6rem]">
                    <CopyField
                      label={
                        t('depositMethods.crypto.copyAddressLabel') as string
                      }
                      value={depositAddressData.cryptoAddress}
                      qrCodeData={`${depositAddressData?.cryptoAddress}`}
                      actions={
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span>
                              <Button
                                variant="secondary"
                                disabled={isGetNewAddressDisabled}
                                onClick={() => setNewAddressStep('confirm')}
                                className="group flex items-center justify-center hover:bg-transparent-bg bg-transparent-bg transition-colors shadow-none size-[4rem] p-[0.8rem] disabled:opacity-40"
                                aria-label={
                                  t(
                                    'depositMethods.crypto.newAddress.tooltip'
                                  ) as string
                                }>
                                <RefreshIcon className="size-[100%] text-white dark:text-primary-foreground group-hover:text-muted-foreground dark:group-hover:text-muted-foreground transition-colors" />
                              </Button>
                            </span>
                          </TooltipTrigger>
                          <TooltipContent
                            side="top"
                            sideOffset={8}
                            className="p4">
                            {t(
                              isNewAddressGenerated
                                ? 'depositMethods.crypto.newAddress.tooltipCreated'
                                : 'depositMethods.crypto.newAddress.tooltip'
                            )}
                          </TooltipContent>
                        </Tooltip>
                      }
                    />
                  </div>
                </div>
              )}
            </>
          )}
        </>
      </ModalComponentWrapper>

      <GetNewAddressDialog
        step={newAddressStep}
        isPending={isGeneratingAddress}
        errorMessage={newAddressError}
        onGenerate={handleGenerateNewAddress}
        onClose={handleNewAddressDialogClose}
      />
    </>
  );
}
